import type * as MapLibre from 'maplibre-gl';
import { IMapLibreOutletMarkerHtmlOptions } from '../models/i-maplibre-outlet-marker-html-options';

const MAPLIBRE_WORKER_URL = 'assets/maplibre-gl-worker.mjs';
const HIDDEN_OUTLET_MAP_STYLE_LAYERS = [
  'housenumbers',
  'building',
  'building-top',
  // 'highway-path',
  'tunnel-path',
  'bridge-path',
  'bridge-path-casing',
  'park-outline',
];

export const configureMapLibreWorker = (maplibre: typeof MapLibre): void => {
  if (maplibre.getWorkerUrl() !== MAPLIBRE_WORKER_URL) {
    maplibre.setWorkerUrl(MAPLIBRE_WORKER_URL);
  }
};

export const simplifyMapLibreOutletStyle = (map: MapLibre.Map): void => {
  map.once('style.load', () => {
    HIDDEN_OUTLET_MAP_STYLE_LAYERS.forEach((layerId) => {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', 'none');
      }
    });
  });
};

export const hasCoordinates = (item: { latitude?: number; longitude?: number }): boolean =>
  Number.isFinite(item?.latitude) && Number.isFinite(item?.longitude);

export const latLng = (item: { latitude: number; longitude: number }): MapLibre.LngLatLike =>
  [item.longitude, item.latitude];

export const createBounds = (
  maplibre: typeof MapLibre,
  items: { latitude?: number; longitude?: number }[],
): MapLibre.LngLatBounds => {
  const bounds = new maplibre.LngLatBounds();

  items
    .filter(hasCoordinates)
    .forEach((item) => bounds.extend(latLng(item as { latitude: number; longitude: number })));

  return bounds;
};

/** Perceived brightness (0-255) of a `#rgb`/`#rgba`/`#rrggbb`/`#rrggbbaa` color; null when unparseable. */
export const getMapLibreColorLuminance = (color: string | null | undefined): number | null => {
  const hex = color?.trim().replace(/^#/, '') || '';

  if (!/^([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(hex)) {
    return null;
  }

  const fullHex = hex.length <= 4 ? hex.split('').map((char) => char + char).join('') : hex;
  const [r, g, b] = [0, 2, 4].map((offset) => parseInt(fullHex.substring(offset, offset + 2), 16));

  return (299 * r + 587 * g + 114 * b) / 1000;
};

export const isDarkMapLibreMarkerColor = (color: string | null | undefined): boolean => {
  const luminance = getMapLibreColorLuminance(color);

  return luminance !== null && luminance <= 128;
};

export const isLightMapLibreMarkerColor = (color: string | null | undefined): boolean => {
  const luminance = getMapLibreColorLuminance(color);

  return luminance !== null && luminance > 128;
};

export const getMapLibreOutletMarkerHtml = (
  outletName: string,
  options: IMapLibreOutletMarkerHtmlOptions = {},
): string => {
  const markerClass = [
    'agm-marker-circle',
    options.isActive ? 'agm-marker-circle__active' : '',
    options.isDisabled && !options.isActive ? 'agm-marker-circle__disabled' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return `
    <div class="map-marker-content" style="width: 48px;">
      <svg xmlns="http://www.w3.org/2000/svg"
           viewBox="0 0 61.67 61.67"
           class="${markerClass}"
           style="${getMapLibreMarkerColorStyle(options)}">
        <title>${escapeMapLibreMarkerHtml(outletName)}</title>

        <circle class="${options.iconUrl ? 'circle' : 'pink-circle'}"
                cx="30.83" cy="30.83" r="29"></circle>
        ${
          options.iconUrl
            ? `<image x="10" y="10" width="43" height="43" href="${escapeMapLibreMarkerHtml(options.iconUrl)}"></image>`
            : `<path class="marker"
                d="M45.69,28.65a1.09,1.09,0,0,1-1.09,1.09h-2L38.2,22.08h3.12a1.1,1.1,0,0,1,.87.44l3.28,4.38a1.08,1.08,0,0,1,.22.65Zm-3.28,17.5H35.85V35.21a1.1,1.1,0,0,0-1.1-1.1h-8.2a1.09,1.09,0,0,0-1.09,1.1V46.15H18.35V31.93H42.41Zm-7.66,0h-8.2V35.21h8.2ZM15.07,28.65v-1.1a1.07,1.07,0,0,1,.21-.65l3.29-4.38a1.1,1.1,0,0,1,.87-.44h3.12l-4.38,7.66h-2a1.09,1.09,0,0,1-1.09-1.09m19.47-6.57h2.4l4.37,7.66H36.73Zm-3.61,0H33.4l2.19,7.66H30.93Zm-3.58,0h2.48v7.66H25.17ZM24,29.74H19.45l4.37-7.66h2.39ZM41.32,19.9H19.44V15.52H41.32Zm5.9,5.68-3.28-4.37a3.19,3.19,0,0,0-.44-.46V15.52a2.19,2.19,0,0,0-2.18-2.19H19.44a2.19,2.19,0,0,0-2.19,2.19v5.23a2.67,2.67,0,0,0-.43.46l-3.28,4.37a3.29,3.29,0,0,0-.66,2v1.1a3.29,3.29,0,0,0,3.28,3.28V46.15a2.19,2.19,0,0,0,2.19,2.18H42.41a2.19,2.19,0,0,0,2.19-2.18V31.93a3.29,3.29,0,0,0,3.28-3.28v-1.1a3.29,3.29,0,0,0-.66-2">
               </path>`
        }
      </svg>
    </div>`;
};

/**
 * Theme colors travel as CSS custom properties so `_map.scss` keeps owning the
 * state rules (disabled stays grey) and falls back to the palette when unset.
 */
const getMapLibreMarkerColorStyle = ({ bgColor, selectedBgColor, glyphColor }: IMapLibreOutletMarkerHtmlOptions): string => [
  isSafeCssColor(bgColor) ? `--map-marker-bg: ${bgColor};` : '',
  isSafeCssColor(selectedBgColor) ? `--map-marker-selected-bg: ${selectedBgColor};` : '',
  isSafeCssColor(glyphColor) ? `--map-marker-glyph: ${glyphColor};` : '',
]
  .filter(Boolean)
  .join(' ');

const isSafeCssColor = (color: string | null | undefined): boolean => Boolean(color) && /^[#\w\s(),.%-]+$/.test(color);

const escapeMapLibreMarkerHtml = (value: any): string => String(value == null ? '' : value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');
